import { useMemo } from 'react';
import { Agent, AgenticProcess, Deployment, TypeId } from '@sdk';
import { useEntity } from '@src/hooks/entity-hooks';

/**
 * The Agent a process was launched through, or null.
 *
 * `AgenticProcess.deployment_id` → `Deployment.agentTypeId` → `Agent`, both
 * hops through `useEntity` (cache-first, live-updating — a rename or a new
 * avatar repaints). A process with no deployment issues no request at all.
 *
 * `enabled` lets a caller that only shows the agent on demand (the Assets
 * popover) defer the two lookups until it is open; the execution panel, which
 * signs every turn with it, leaves it on.
 */
export function useLaunchingAgent(
  deploymentId: string | null | undefined,
  options?: { enabled?: boolean },
): Agent | null {
  const enabled = options?.enabled !== false;
  const deploymentTypeId = useMemo(
    () => (deploymentId ? new TypeId(Deployment.type, deploymentId) : null),
    [deploymentId],
  );
  const deployment = useEntity<Deployment>(deploymentTypeId, { enabled }).data;
  const agentTypeId = deployment?.agentTypeId ?? null;
  return useEntity<Agent>(agentTypeId, { enabled }).data ?? null;
}

/**
 * The vibe-only Agent (`Agent.vibe_only`) the session `processId` was launched
 * through, or null. Such a session stays in Vibe: no tab strip, no surface switch.
 */
export function useVibeOnlyAgent(processId: string | null | undefined): Agent | null {
  const typeId = useMemo(() => (processId ? new TypeId(AgenticProcess.type, processId) : null), [processId]);
  const process = useEntity<AgenticProcess>(typeId).data;
  const agent = useLaunchingAgent(process?.deployment_id);
  return agent?.vibe_only ? agent : null;
}
